'use client';

import React, { useEffect, useRef } from 'react';

export function ControlBarAutoHide() {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringRef = useRef(false);
  const hasMovedMouseRef = useRef(false);

  useEffect(() => {
    const HIDE_DELAY = 2000; // 2 segundos sem movimento para esconder

    let controlBar: HTMLElement | null = null;
    let videoConference: HTMLElement | null = null;

    const showBar = () => {
      if (!controlBar) return;
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      controlBar.style.opacity = '1';
      controlBar.style.pointerEvents = 'auto';
      controlBar.style.transform = 'translateX(-50%) translateY(0)';
      controlBar.style.visibility = 'visible';
      controlBar.style.maxHeight = 'none';
      controlBar.style.padding = '';
      controlBar.style.margin = '';
      
      // Restaura overflow normal quando a barra aparece
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      
      const roomContainer = document.querySelector('.lk-room-container') as HTMLElement;
      if (roomContainer) {
        roomContainer.style.overflow = '';
      }
      
      const videoConference = document.querySelector('.lk-video-conference') as HTMLElement;
      if (videoConference) {
        videoConference.style.overflow = 'hidden';
      }
      
      const gridLayout = document.querySelector('.lk-grid-layout') as HTMLElement;
      if (gridLayout) {
        gridLayout.style.overflow = 'hidden';
      }
    };

    const hideBar = () => {
      if (!controlBar || isHoveringRef.current) return;
      
      controlBar.style.opacity = '0';
      controlBar.style.pointerEvents = 'none';
      controlBar.style.transform = 'translateX(-50%) translateY(20px)';
      controlBar.style.visibility = 'hidden';
      controlBar.style.maxHeight = '0';
      controlBar.style.padding = '0';
      controlBar.style.margin = '0';
      
      // Força overflow hidden em todos os containers para evitar scrollbars
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      const roomContainer = document.querySelector('.lk-room-container') as HTMLElement;
      if (roomContainer) {
        roomContainer.style.overflow = 'hidden';
      }
      
      const videoConference = document.querySelector('.lk-video-conference') as HTMLElement;
      if (videoConference) {
        videoConference.style.overflow = 'hidden';
      }
      
      const gridLayout = document.querySelector('.lk-grid-layout') as HTMLElement;
      if (gridLayout) {
        gridLayout.style.overflow = 'hidden';
      }
    };

    const scheduleHide = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(hideBar, HIDE_DELAY);
    };

    const handleMouseMove = (event: MouseEvent) => {
      hasMovedMouseRef.current = true;
      if (event.clientY >= window.innerHeight - 110) {
        showBar();
      }
      showBar();
      scheduleHide();
    };

    const handleMouseEnter = () => {
      isHoveringRef.current = true;
      showBar();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const handleMouseLeave = () => {
      isHoveringRef.current = false;
      scheduleHide();
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (!controlBar) return;
      const barRect = controlBar.getBoundingClientRect();
      const nearDock =
        event.clientY >= window.innerHeight - 120 ||
        (barRect.width > 0 &&
          event.clientX >= barRect.left - 24 &&
          event.clientX <= barRect.right + 24 &&
          event.clientY >= barRect.top - 24 &&
          event.clientY <= barRect.bottom + 24);

      if (nearDock) {
        showBar();
      }
    };

    const setup = () => {
      controlBar = document.querySelector('.lk-control-bar') as HTMLElement;
      videoConference = document.querySelector('.lk-video-conference') as HTMLElement;
      
      if (!controlBar || !videoConference) return false;

      // Força estado inicial escondido
      controlBar.style.transition = 'opacity 0.3s ease, transform 0.3s ease, visibility 0.3s ease, max-height 0.3s ease, padding 0.3s ease, margin 0.3s ease';
      controlBar.style.opacity = '0';
      controlBar.style.pointerEvents = 'none';
      controlBar.style.transform = 'translateX(-50%) translateY(20px)';
      controlBar.style.visibility = 'hidden';
      controlBar.style.maxHeight = '0';
      controlBar.style.padding = '0';
      controlBar.style.margin = '0';
      
      // Força overflow hidden inicialmente
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      const roomContainer = document.querySelector('.lk-room-container') as HTMLElement;
      if (roomContainer) {
        roomContainer.style.overflow = 'hidden';
      }
      
      const gridLayout = document.querySelector('.lk-grid-layout') as HTMLElement;
      if (gridLayout) {
        gridLayout.style.overflow = 'hidden';
      }

      // Event listeners
      videoConference.addEventListener('mousemove', handleMouseMove, { passive: true });
      controlBar.addEventListener('mouseenter', handleMouseEnter);
      controlBar.addEventListener('mouseleave', handleMouseLeave);
      document.addEventListener('pointerdown', handlePointerDown, true);

      return true;
    };

    // Tenta configurar imediatamente
    if (setup()) {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        if (videoConference) {
          videoConference.removeEventListener('mousemove', handleMouseMove);
        }
        if (controlBar) {
          controlBar.removeEventListener('mouseenter', handleMouseEnter);
          controlBar.removeEventListener('mouseleave', handleMouseLeave);
        }
        document.removeEventListener('pointerdown', handlePointerDown, true);
      };
    }

    // Se não encontrar, usa MutationObserver para detectar quando aparecer
    const observer = new MutationObserver(() => {
      if (!controlBar || !videoConference) {
        if (setup()) {
          observer.disconnect();
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Também tenta periodicamente
    const interval = setInterval(() => {
      if (setup()) {
        clearInterval(interval);
        observer.disconnect();
      }
    }, 200);

    return () => {
      observer.disconnect();
      clearInterval(interval);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (videoConference) {
        videoConference.removeEventListener('mousemove', handleMouseMove);
      }
      if (controlBar) {
        controlBar.removeEventListener('mouseenter', handleMouseEnter);
        controlBar.removeEventListener('mouseleave', handleMouseLeave);
      }
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, []);

  return null;
}
